// Conversation Engine
// Handles the AI conversation loop with tool execution

import { callAPI } from '../api';
import { executeTool, getToolDisplayMessage } from '../tools';
import { filterForAPI } from '../types/messages';

const MAX_ITERATIONS = 20;

export async function runConversation(messages, userMessage, context, callbacks) {
  const { onToolCall, onToolResult, onAssistantMessage } = callbacks;

  // System prompt and tool defs are owned by the server (server-authoritative brain)
  const apiMessages = [
    ...filterForAPI(messages),
    { role: 'user', content: userMessage }
  ];

  let iterations = 0;

  while (iterations < MAX_ITERATIONS) {
    iterations++;

    // Call API
    const response = await callAPI(apiMessages);
    const assistantMessage = response.choices[0]?.message;

    if (!assistantMessage) {
      throw new Error('Empty response from AI');
    }

    // Check for tool calls
    if (assistantMessage.tool_calls && assistantMessage.tool_calls.length > 0) {
      // Notify about tool calls
      onToolCall(assistantMessage.tool_calls);

      // Add assistant message to conversation
      apiMessages.push({
        role: 'assistant',
        content: assistantMessage.content,
        tool_calls: assistantMessage.tool_calls
      });

      // Execute each tool. Every tool_call MUST yield exactly one tool result,
      // even on malformed args or a throwing tool, so the assistant tool_calls
      // and their results are never left unpaired in state (history poisoning).
      for (const toolCall of assistantMessage.tool_calls) {
        const { name } = toolCall.function;

        let args;
        let argsValid = true;
        try {
          args = JSON.parse(toolCall.function.arguments);
        } catch (err) {
          argsValid = false;
          args = {};
        }

        let result;
        let displayText;
        if (!argsValid) {
          result = {
            success: false,
            error: `Invalid tool arguments: could not parse JSON for ${name}`
          };
          displayText = getToolDisplayMessage(name, {});
        } else {
          displayText = getToolDisplayMessage(name, args);
          try {
            result = await executeTool(name, args, context);
          } catch (err) {
            result = {
              success: false,
              error: `Tool execution failed: ${err.message}`
            };
          }
        }

        const resultStr = JSON.stringify(result);

        // Notify about result
        onToolResult(toolCall.id, name, result, displayText);

        // Add tool result to conversation
        apiMessages.push({
          role: 'tool',
          tool_call_id: toolCall.id,
          content: resultStr
        });
      }

      // Continue loop for next AI response
      continue;
    }

    // No tool calls - this is the final response
    if (assistantMessage.content) {
      onAssistantMessage(assistantMessage.content);
      return assistantMessage.content;
    }

    throw new Error('AI response has no content or tool calls');
  }

  throw new Error(`Conversation exceeded ${MAX_ITERATIONS} iterations`);
}
